const STORAGE_KEY_USER = 'userData';
const STORAGE_KEY_MODULES_PREFIX = 'userModules_';
const API_BASE_URL = 'https://lms.akiliapp.co.tz/api';

export const DEFAULT_MODULES = [
  { code: 'HMU08001', name: 'Innovation Management', status: 'Start', progress: 0, route: '/modules/hmu08001' },
  { code: 'HMU08002', name: 'Intellectual Property (IP) Management', status: 'Start', progress: 0, route: '/modules/hmu08002' },
  { code: 'HMU08003', name: 'Research Commercialization', status: 'Finished', progress: 100, route: '/modules/hmu08003' },
  { code: 'HMU08004', name: 'Fundraising and Sustainable Hub Operations', status: 'Finished', progress: 100, route: '/modules/hmu08004' }
];

export const getUserData = () => {
  const userData = localStorage.getItem(STORAGE_KEY_USER);
  return userData ? JSON.parse(userData) : null;
};

export const setUserData = (userData) => {
  localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(userData));
};

export const clearUserData = () => {
  localStorage.removeItem(STORAGE_KEY_USER);
};

export const normalizeRole = (role) => {
  const r = (role || 'User').toString().trim();
  return /admin|administrator/i.test(r) ? 'Admin' : 'User';
};

function emailsMatch(a, b) {
  if (!a || !b) return false;
  return a.toString().toLowerCase().trim() === b.toString().toLowerCase().trim();
}

async function fetchUserIget(accessToken) {
  const { default: axios } = await import('axios');
  const { data } = await axios.get(`${API_BASE_URL}/users/iget`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json'
    }
  });
  if ((data?.status ?? '').toString().toUpperCase() !== 'OK') return null;
  return data?.returnData?.item ?? data?.returnData ?? null;
}

async function fetchUserByEmail(accessToken, email) {
  const { default: axios } = await import('axios');
  const target = email.toString().toLowerCase().trim();
  let page = 1;
  let lastPage = 1;

  while (page <= lastPage && page <= 10) {
    const { data } = await axios.get(`${API_BASE_URL}/users/ilist`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json'
      },
      params: { paginate: true, per_page: 50, page }
    });
    if ((data?.status ?? '').toString().toUpperCase() !== 'OK') return null;

    const rd = data?.returnData ?? {};
    const list = rd.list_of_item ?? rd;
    const items = Array.isArray(list?.data) ? list.data : (Array.isArray(rd.data) ? rd.data : []);
    const match = items.find((row) => emailsMatch(row.email, target));
    if (match) return match;

    const meta = list?.meta ?? rd.meta ?? {};
    lastPage = Number(meta.last_page ?? meta.lastPage ?? 1) || 1;
    page += 1;
  }

  return null;
}

/** Resolve profile for the logged-in email; avoids wrong user/role from /users/iget. */
export const resolveUserProfile = async (accessToken, loginEmail, fallback = {}) => {
  const email = (loginEmail || fallback.email || '').toString().toLowerCase().trim();
  if (!accessToken || !email) return null;

  let profile = await fetchUserIget(accessToken);
  if (!profile || !emailsMatch(profile.email, email)) {
    const byEmail = await fetchUserByEmail(accessToken, email);
    if (byEmail) {
      profile = byEmail;
    } else if (!profile) {
      profile = { email, role: fallback.role ?? 'User' };
    } else {
      profile = {
        ...profile,
        email,
        role: fallback.role ?? profile.role ?? 'User'
      };
    }
  }

  const role = normalizeRole(profile.role ?? fallback.role ?? 'User');
  const name = (profile.name ?? fallback.name ?? email.split('@')[0]).toString().trim() || email;

  return {
    id: profile.id != null ? Number(profile.id) : (fallback.id != null ? Number(fallback.id) : undefined),
    name,
    email,
    phone: profile.phone ?? fallback.phone ?? '',
    role
  };
};

/** Refresh stored user from API (fixes wrong id/role when /users/iget returns another account). */
export const refreshUserProfile = async () => {
  const user = getUserData();
  if (!user?.access_token) return user;

  try {
    const resolved = await resolveUserProfile(user.access_token, user.email, user);
    if (!resolved) return user;

    const updated = {
      ...user,
      ...resolved,
      access_token: user.access_token
    };
    setUserData(updated);
    return updated;
  } catch {
    return user;
  }
};

export const getModulesForUser = (userId) => {
  if (!userId) return [];
  const key = `${STORAGE_KEY_MODULES_PREFIX}${userId}`;
  const stored = localStorage.getItem(key);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (_) {
      return [...DEFAULT_MODULES];
    }
  }
  localStorage.setItem(key, JSON.stringify(DEFAULT_MODULES));
  return [...DEFAULT_MODULES];
};
